import { logger } from './logger';
import api from './api';
import type { ObjectMetadataValue } from '../types/ObjectHierarchicalMetadataValue';

/**
 * Bulk metadata edit API.
 *
 * Applies a SUBSET of shareable metadata fields to many catalog items at once
 * via PUT /api/metadata/bulk. Only fields present in `fields` are written;
 * untouched fields are left unchanged on each object. List fields honor
 * `listMode` ("add" unions with existing, "replace" overwrites); single-value
 * fields (creationYear, workType) replace. Delegation is supported via the
 * optional `ownerId` query param (mirrors the bulk visibility endpoint).
 */

/** How list-valued fields combine with each object's existing values. */
export type BulkEditListMode = 'add' | 'replace';

/**
 * A provenance record to APPEND to each selected object's ownership chain.
 * Bulk provenance always appends (existing history is preserved) and is NOT
 * governed by {@link BulkEditListMode}.
 */
export interface BulkProvenanceRecord {
  publicRendering: string;
  internalRendering: string;
  isPublic: boolean;
}

/**
 * The shareable fields a bulk edit may write. Every field is optional —
 * omit a field to leave it unchanged on every selected object. Title, ALI
 * catalog number, and creators are intentionally absent (unique per object).
 *
 * `markedAsSold` is a tri-state set: omit = leave unchanged, true/false =
 * overwrite the flag on every object (not governed by `listMode`). `provenance`
 * records are always APPENDED to each object's chain (also ignores `listMode`).
 */
export interface BulkEditFields {
  /** Year string; stored as `creationDate` with type "Year". */
  creationYear?: string;
  /** Single Getty AAT work-type value. */
  workType?: ObjectMetadataValue;
  materials?: ObjectMetadataValue[];
  techniques?: ObjectMetadataValue[];
  substrate?: ObjectMetadataValue[];
  keywords?: string[];
  /** Tri-state sold flag; omit to leave unchanged. */
  markedAsSold?: boolean;
  /** Provenance records appended to every selected object's chain. */
  provenance?: BulkProvenanceRecord[];
}

export interface BulkEditResponse {
  message: string;
  updated: number;
  failed: number;
  failedIds: string[];
}

export const bulkEditObjects = async (
  artworkIds: string[],
  fields: BulkEditFields,
  listMode: BulkEditListMode,
  ownerId?: string | null,
): Promise<BulkEditResponse> => {
  try {
    const params = ownerId ? `?owner_id=${ownerId}` : '';
    const response = await api.put(`/api/metadata/bulk${params}`, {
      artworkIds,
      listMode,
      ...fields,
    });
    return response.data;
  } catch (error) {
    logger.error('[BulkEdit API] Failed to bulk edit objects', error);
    throw error;
  }
};
